import { CliError } from './errors';
import {
  LEGACY_ALL_SCOPE,
  EXTENSION_TYPE_ACTION_LINK,
  EXTENSION_TYPE_IFRAME,
  UPLOADABLE_LINK_TARGETS,
} from './constants';

const APP_NAME_MAX_LENGTH = 48;
const APP_NAME_REGEX = /^[a-zA-Z0-9 ._\-\u00C0-\u024F]+$/;

/**
 * Validate an app name: alphanumeric, spaces, hyphens, dots, underscores,
 * and accented/extended Latin characters only. Max 48 characters.
 * Returns true if valid, or an error string for inquirer prompts.
 */
export function validateAppName(name: string): true | string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'App name cannot be empty.';
  if (trimmed.length > APP_NAME_MAX_LENGTH) {
    return `App name must be at most ${APP_NAME_MAX_LENGTH} characters (got ${trimmed.length}).`;
  }
  if (!APP_NAME_REGEX.test(trimmed)) {
    return 'App name can only contain letters, numbers, spaces, hyphens, dots, underscores, and accented characters.';
  }
  return true;
}

/**
 * Validate a free-text y/n answer, empty meaning "take the default".
 *
 * Shared rather than local to one command because two commands ask this shape of
 * question in two files (`app create`'s "add another redirect URL?" and the
 * scaffold-a-feature confirm both `app create` and `app scaffold` use), and they
 * must accept exactly the same spellings.
 */
export function validateYesNo(input: string): true | string {
  const val = String(input).toLowerCase().trim();
  if (val === 'y' || val === 'yes' || val === 'n' || val === 'no' || val === '') {
    return true;
  }
  return 'Please enter y or n';
}

/**
 * Validate that a value is one of the allowed options.
 * Throws CliError with a user-friendly message if invalid.
 */
export function validateEnum(
  value: string | undefined,
  allowed: readonly string[],
  flagName: string,
): void {
  if (value && !allowed.includes(value)) {
    throw new CliError(`Invalid ${flagName} "${value}". Must be one of: ${allowed.join(', ')}.`);
  }
}

/**
 * Validate that a string is a valid HTTP/HTTPS URL.
 * Throws CliError if the format is invalid.
 */
export function validateUrl(value: string | undefined, fieldName: string): void {
  if (!value) return;
  // Reject whitespace and commas early — Node's URL parser silently percent-encodes
  // spaces, so "http://a/cb, http://b/cb" parses as a single valid URL. That has let
  // comma-separated values sneak into redirect_uris as one corrupted entry.
  if (/[\s,]/.test(value)) {
    throw new CliError(
      `Invalid ${fieldName}: "${value}" contains whitespace or a comma. Pass each URL with a separate --redirect-uri flag.`,
    );
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error('bad protocol');
    }
  } catch {
    throw new CliError(`Invalid ${fieldName}: "${value}" is not a valid HTTP/HTTPS URL.`);
  }
}

/**
 * Commander.js collect function for repeatable --redirect-uri flags.
 * Validates each URL and accumulates into an array.
 */
export function collectUrls(value: string, previous: string[] = []): string[] {
  validateUrl(value, 'redirect URL');
  return [...previous, value];
}

// OAuth scope tokens are split on commas and whitespace at every boundary
// (app-config.json reads and --scope flag values) so a user can write either
// "crm:read crm:write" or "crm:read, crm:write" or one --scope per token and
// the CLI behaves the same. RFC 6749 §3.3 already mandates space-separation in
// authorization requests, so the split is consistent with the protocol; the
// comma is a convenience for users editing JSON arrays.
const SCOPE_TOKEN_REGEX = /^[A-Za-z0-9][A-Za-z0-9:_.-]*$/;
const SCOPE_SPLIT_REGEX = /[\s,]+/;

/**
 * Split a scope string or array of strings into individual scope tokens.
 * Handles embedded commas/whitespace, trims, drops empties, dedupes.
 * Does NOT validate token format — use `validateScopes` for that.
 */
export function splitScopes(input: string | string[] | undefined | null): string[] {
  if (input == null) return [];
  const values = Array.isArray(input) ? input : [input];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    if (typeof v !== 'string') continue;
    for (const token of v.split(SCOPE_SPLIT_REGEX)) {
      if (!token) continue;
      if (seen.has(token)) continue;
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

/**
 * Validate that each scope is a well-formed token.
 * Throws CliError with a user-facing message if any scope is invalid.
 */
export function validateScopes(scopes: string[]): void {
  for (const scope of scopes) {
    if (!SCOPE_TOKEN_REGEX.test(scope)) {
      throw new CliError(
        `Invalid scope: "${scope}" — scopes can only contain letters, numbers, ':', '_', '.', '-'.`,
      );
    }
  }
}

/**
 * Commander.js collect function for repeatable --scope flags.
 * Splits each value on commas/whitespace, validates the resulting tokens,
 * and accumulates into an array (deduplicated against previous values).
 */
export function collectScopes(value: string, previous: string[] = []): string[] {
  const tokens = splitScopes(value);
  if (tokens.length === 0) {
    throw new CliError('Invalid scope: value cannot be empty.');
  }
  validateScopes(tokens);
  const out = [...previous];
  for (const t of tokens) {
    if (!out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * Returns true iff the scope list contains the deprecated legacy 'all' scope.
 * Every code path that warns or blocks on the legacy scope calls this helper —
 * no scattered string literals (BEX-214).
 */
// Accepts null as well as undefined: the app-store read path returns `scopes: null`
// for an app with no OAuth block, and both mean "no scopes" here.
export function containsLegacyAllScope(scopes: string[] | null | undefined): boolean {
  return scopes?.includes(LEGACY_ALL_SCOPE) ?? false;
}

// ──────────────── UI apps (BEX-290) ────────────────

/**
 * Whether an HTTP(S) URL is safe as a UI-app destination.
 *
 * The extensibility UI kit drops any non-http(s) `redirect_link` outright
 * (`isHttpUrl` in its shared utils), so anything else would be authored and then
 * silently ignored. On top of that, Brevo opens this URL from an authenticated
 * CRM page, so plain http would downgrade the session — https is required, with
 * the loopback exemption the rest of the CLI grants so partners can point at a
 * local dev server while building.
 */
function isSafeUiAppUrl(parsed: URL): boolean {
  if (parsed.protocol === 'https:') return true;
  return (
    parsed.protocol === 'http:' &&
    (parsed.hostname === 'localhost' ||
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname === '::1')
  );
}

/**
 * Validate a UI-app destination URL. Returns `true` or an error string, so it can
 * back an inquirer `validate` directly as well as the upload-time check.
 */
export function validateUiAppUrl(value: string): true | string {
  const trimmed = value.trim();
  if (!trimmed) return 'URL cannot be empty.';
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return `Invalid URL: "${trimmed}" is not a valid URL.`;
  }
  if (!isSafeUiAppUrl(parsed)) {
    return `Invalid URL: "${trimmed}" must use https:// (http:// is allowed only for localhost).`;
  }
  return true;
}

// Length ceilings for the two authored text fields. Both are enforced server-side, so
// without a local check the partner gets an opaque 400 from the upload endpoint — exactly
// the failure mode this pre-flight exists to prevent. `label` shares the app-name ceiling
// because it occupies the same one-line menu row.
const UI_APP_LABEL_MAX_LENGTH = 48;
const UI_APP_MORE_INFO_MAX_LENGTH = 255;

/**
 * Validate a UI-app `label` — the menu entry's text on an `.action` slot, the card's CTA
 * button text on a `.widget` slot. Returns `true` or an error string, so it can back an
 * inquirer `validate` directly.
 */
export function validateUiAppLabel(value: string): true | string {
  const trimmed = value.trim();
  if (!trimmed) return 'Label cannot be empty.';
  if (trimmed.length > UI_APP_LABEL_MAX_LENGTH) {
    return `Label must be at most ${UI_APP_LABEL_MAX_LENGTH} characters (got ${trimmed.length}).`;
  }
  return true;
}

/**
 * Validate a UI-app `more_info` — the menu entry's `subText`, the card's description.
 * Optional, so blank passes; only the length ceiling is enforced.
 */
export function validateUiAppMoreInfo(value: string): true | string {
  const trimmed = value.trim();
  if (trimmed.length > UI_APP_MORE_INFO_MAX_LENGTH) {
    return `More info must be at most ${UI_APP_MORE_INFO_MAX_LENGTH} characters (got ${trimmed.length}).`;
  }
  return true;
}

/**
 * Validate the SHAPE of a `surface_point_list` entry's slot name — present and
 * non-blank. Whether the name is registered is deliberately NOT checked here.
 *
 * The registry is the platform's, and only the platform can answer against it
 * without lagging. `app upload` sends the block and the upload endpoint checks
 * every name against `extension_points` in one read, answering 400 with the
 * offending names (`checkExtensionPoints`, BEX-361). `app create` cannot author
 * an unregistered name in the first place: every entry it writes is built from a
 * row the registry just returned.
 *
 * The failure this guards against is still real — the platform DROPS an
 * unregistered name, so a near-miss renders nothing with a 200 — but a local
 * allow-list was the wrong place to catch it. It could only ever be a stale
 * copy, and it failed in both directions: rejecting a slot the platform had added,
 * and passing one the platform had removed.
 *
 * Note the value is the registry's `surface_point_name` slug
 * (`contactDetails.header.menu`), not the `<location>.<place>.<kind>` grammar name
 * (`contactDetails.headerMenu.action`) — see `SurfacePointEntry.surface_point_name`.
 * Shape-only means the two are indistinguishable
 * here; only the registry can tell them apart, which is another reason not to try.
 */
export function validateSurfacePoint(point: string): true | string {
  const trimmed = String(point ?? '').trim();
  if (!trimmed) return 'Surface point cannot be empty.';
  return true;
}

/**
 * The extension types the CLI can author. `legacyComponent` is absent by design: it is
 * the pre-extensibility interpreter path, driven by the UI kit's own config registry
 * rather than by a snapshot, and never partner-authored.
 *
 * The pre-BEX-350 snake_case spellings are absent too — the CLI only ever writes
 * canonical camelCase, even though the server still accepts the old spellings inbound.
 */
const AUTHORABLE_EXTENSION_TYPES: readonly string[] = [
  EXTENSION_TYPE_ACTION_LINK,
  EXTENSION_TYPE_IFRAME,
] as const;

/**
 * Validate one placement's `context` list: field names must be non-empty and unique.
 *
 * Shape only. Whether the platform actually ALLOWS a given name is not checkable locally —
 * the allow-list lives on that extension-point registry row — so it is checked server-side
 * at upload, where the 400 enumerates the allowed set. `brevo app create` never produces a
 * disallowed name anyway: it seeds each entry from the row's own `default_context_field`,
 * which the registry keeps inside the row's allow-list.
 */
export function validateUiAppContext(fields: readonly string[]): true | string {
  const seen = new Set<string>();
  for (const field of fields) {
    const trimmed = String(field ?? '').trim();
    if (!trimmed) return 'Context field names cannot be empty.';
    if (seen.has(trimmed)) return `Duplicate context field "${trimmed}".`;
    seen.add(trimmed);
  }
  return true;
}

/**
 * `String()` for a field read out of a hand-edited `app-config.json`, type-narrowed first.
 *
 * Every field of `ui_app` arrives as `unknown`, and a bare `String(value)` on an object or
 * array yields Object's default stringification — `'[object Object]'`. That is a non-blank
 * string, so it sails through the "cannot be empty" checks below and lets an object reach
 * the wire under a field the server expects to be text. Anything that is not a primitive is
 * therefore treated as absent, so the field's own message fires instead.
 *
 * Narrowed by listing what IS accepted rather than excluding objects, because TypeScript
 * does not subtract from `unknown`: after `if (typeof value === 'object') return ''` the
 * else-branch is still `unknown`, so `String(value)` there is the very call this exists to
 * avoid. A JSON document holds no symbols or functions, so nothing reachable is lost.
 */
function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

/**
 * Is a field that must be ABSENT actually present?
 *
 * Distinct from `asText(value).trim() !== ''` on purpose: these are the "cannot be combined
 * with" checks, where a non-string value is still something the partner wrote and still
 * travels to the wire, so it must be reported rather than read as an empty string and
 * waved through. Only a genuinely blank string counts as absent.
 */
function isPresentField(value: unknown): boolean {
  if (value === undefined) return false;
  return typeof value !== 'string' || value.trim() !== '';
}

/**
 * Fully validate a `ui_app` block before it is sent to the server.
 *
 * The block is the app snapshot the platform stores, verbatim.
 * Every field below is optional on the wire — the backend degrades a malformed or
 * absent snapshot to "not yet migrated" rather than erroring — which means the
 * serving path will NOT tell a partner their extension is unrenderable. This
 * pre-flight is therefore deliberately stricter than the wire: it requires the fields
 * each type actually needs to render, and rejects combinations the consumers silently
 * discard.
 *
 * Throws CliError on the first problem found.
 */
export function validateUiApp(uiApp: unknown): void {
  if (!uiApp || typeof uiApp !== 'object') {
    throw new CliError(
      'app-config.json has an invalid "ui_app" block — expected an object. Fix the file, or recreate the app with `brevo app create` and choose "UI app".',
    );
  }
  const block = uiApp as Record<string, unknown>;
  const extensionType = asText(block.extension_type);

  if (!AUTHORABLE_EXTENSION_TYPES.includes(extensionType)) {
    throw new CliError(
      `Unsupported ui_app.extension_type "${extensionType}". Must be one of: ${AUTHORABLE_EXTENSION_TYPES.join(', ')}.`,
    );
  }

  rejectPreBex290Fields(block);
  rejectRootCtaFields(block);

  validateSurfacePointList(block.surface_point_list, extensionType);
}

/**
 * Refuse the pre-BEX-290 field names with a migration hint rather than a mystery.
 *
 * These are a local diagnostic and claim nothing about how the upload endpoint treats an
 * unmigrated block. They earn their place on the message alone: `label`, `more_info` and a
 * per-placement `context` are the only names any consumer reads, so a config written by an
 * earlier build of this branch is wrong whatever the server does with it, and the generic
 * "label cannot be empty" the checks below pre-empt points at the wrong thing — the label
 * IS there, under its old name.
 *
 * No read-path alias, deliberately, for the same reason the snake_case rename didn't get
 * one: both renames shipped BEFORE UI apps went GA (BEX-290), so the old names never
 * existed in a partner config — only on developer machines, where a two-line rename is
 * cheaper to explain than an alias map that has to be removed later. That reasoning is
 * frozen at GA: now that partner configs exist in the wild, a future rename DOES need a
 * migration path, not this treatment.
 */
function rejectPreBex290Fields(block: Record<string, unknown>): void {
  if (block.heading !== undefined) {
    throw new CliError(
      "ui_app.heading was renamed to ui_app.label (it is the menu entry's text and the card's CTA). Rename the field in app-config.json.",
    );
  }
  if (block.subheading !== undefined) {
    throw new CliError(
      "ui_app.subheading was renamed to ui_app.more_info (it is the menu entry's second line and the card's description). Rename the field in app-config.json.",
    );
  }
  if (block.context !== undefined) {
    throw new CliError(
      'ui_app.context is no longer a top-level field — record context is now per placement. Move each field list into the matching `surface_point_list` entry, e.g. [{ "surface_point_name": "contactDetails.header.menu", "context": ["recordId"] }].',
    );
  }
}

/**
 * Refuse the four CTA fields at the `ui_app` root with a migration hint (BEX-426).
 *
 * `label`, `more_info`, `redirect_link` and `modal_iframe_url` moved into each
 * `surface_point_list` entry so an app on three slots can label each differently and
 * deep-link each somewhere else — the same move `context` and `size` already made, for
 * the same reason. Hard move, no root fallback: a root value silently mirrored onto
 * every entry would be a second placement for the same fact, and the server refuses
 * the root spellings by name too, so accepting them here would only defer the 400.
 *
 * Named refusals rather than "unknown field" because a config written by an earlier
 * build is wrong in a very specific way — the value IS there, one level up — and the
 * per-entry "label cannot be empty" it would otherwise hit points at the wrong thing.
 */
function rejectRootCtaFields(block: Record<string, unknown>): void {
  const moved: ReadonlyArray<[key: string, hint: string]> = [
    ['label', '"label": "Open in Acme"'],
    ['more_info', '"more_info": "See this record in Acme"'],
    ['redirect_link', '"redirect_link": "https://example.com/open"'],
    ['modal_iframe_url', '"modal_iframe_url": "https://example.com/embed"'],
  ];
  for (const [key, hint] of moved) {
    if (block[key] !== undefined) {
      throw new CliError(
        `ui_app.${key} moved into each surface_point_list entry (each placement carries its own) — e.g. [{ "surface_point_name": "contactDetails.header.menu", ${hint} }]. Move it in app-config.json.`,
      );
    }
  }
  // `link_target` moved onto each entry with them, but its migration hint is "delete it",
  // not "move it": the CLI has never wanted this field in the file (`app upload` injects
  // it per entry, `app create` never writes it, the write-back strips it), so telling a
  // partner to relocate a value they should not be authoring would be the wrong advice.
  // The server refuses the root spelling by name too — `supersededUIAppKeys` in bo-be.
  if (block.link_target !== undefined) {
    throw new CliError(
      'ui_app.link_target moved onto each surface_point_list entry (BEX-426), and is not authored in app-config.json at all — `brevo app upload` injects it per placement. Remove it from the file.',
    );
  }
}

/**
 * Validate the slot list. Both extension types render on both kinds — a widget slot gets
 * a card, an action slot a menu entry — so the rules are that the list is non-empty, every
 * entry is an object naming a slot, no slot repeats, and each entry's `context` (when
 * present) is a well-formed list of field names.
 *
 * Since BEX-426 each entry also carries its own CTA fields, so the per-type rules run
 * per entry (`extensionType` selects which set): an `actionLink` entry needs `label` and
 * `redirect_link` and must not carry `modal_iframe_url`; an `iframeExtension` entry needs
 * `label` and `modal_iframe_url` and must not carry `redirect_link`. Every message names
 * the offending entry — "ui_app.redirect_link is required" is useless once there are
 * three of them.
 *
 * Shape only: whether a name is registered, and whether its context is within that slot's
 * allow-list, are both the upload endpoint's answer to give (see `validateSurfacePoint`).
 */
function validateSurfacePointList(entries: unknown, extensionType: string): void {
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new CliError(
      'ui_app.surface_point_list must list at least one placement (e.g. [{ "surface_point_name": "contactDetails.header.menu", "context": ["recordId"] }]). An empty list makes the platform fall back to its default widget slots, which is unlikely to be where you want the app.',
    );
  }
  const names: string[] = [];
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new CliError(
        'ui_app.surface_point_list entries must be objects, e.g. { "surface_point_name": "contactDetails.header.menu", "context": ["recordId"] }. A bare string is the pre-BEX-290 shape.',
      );
    }
    const row = entry as Record<string, unknown>;
    // A missing key is a different mistake from a blank value, and the likeliest cause is
    // the `surface_point` spelling — which is used nowhere: not authored, not a hint, not
    // on the registry response. Name the rename rather than reporting the name as blank.
    if (row.surface_point_name === undefined) {
      throw new CliError(
        'ui_app.surface_point_list entries must carry "surface_point_name" (e.g. { "surface_point_name": "contactDetails.header.menu" }).' +
          (row.surface_point !== undefined
            ? ' "surface_point" is not a field — rename it to "surface_point_name".'
            : ''),
      );
    }
    const check = validateSurfacePoint(asText(row.surface_point_name));
    if (check !== true) throw new CliError(`ui_app.surface_point_list: ${check}`);
    const name = asText(row.surface_point_name).trim();
    names.push(name);

    validateEntryContext(row, name);
    validateEntrySize(row, name);
    validateEntryCtaFields(row, name, extensionType);
  }
  if (new Set(names).size !== names.length) {
    throw new CliError('ui_app.surface_point_list contains duplicate extension points.');
  }
}

// One authored size axis: a positive integer with an explicit px or % unit. The unit is
// mandatory — a bare number is refused rather than defaulted, so the authored value is
// always a CSS length the record page can apply verbatim.
const SIZE_AXIS_PATTERN = /^([1-9]\d*)(px|%)$/;

/**
 * Validate one entry's authored card size: an object with `width` and/or `height`, each a
 * CSS length string — "<positive integer>px" (absolute) or "<1-100>%" (relative to the host
 * slot's box). Both axes are optional: an omitted axis stays on the host slot's default, and
 * an empty object authors nothing at all — the server stores it as no size, same as omitting
 * the key. Percentages are capped at 100: the design is shrink-only, and a >100% axis is a
 * request to grow past the slot. The platform enforces the same grammar, so accepting more
 * here would only defer the 400 to `app upload`.
 */
function validateSurfacePointSize(size: unknown): true | string {
  if (!size || typeof size !== 'object' || Array.isArray(size)) {
    return 'must be an object, e.g. { "width": "280px", "height": "160px" }.';
  }
  const { width, height } = size as Record<string, unknown>;
  for (const [axis, value] of Object.entries({ width, height })) {
    if (value === undefined) continue;
    const match = typeof value === 'string' ? SIZE_AXIS_PATTERN.exec(value) : null;
    if (!match) {
      return `${axis} must be a positive integer with a px or % unit, e.g. "280px" or "50%".`;
    }
    if (match[2] === '%' && Number(match[1]) > 100) {
      // `match[0]` rather than `value`: the pattern is anchored, so the whole match IS
      // the authored string — and it is typed `string`, so the message can never quote
      // an `unknown`'s default stringification ("[object Object]").
      return `${axis} "${match[0]}" is out of range — a % axis must be between 1% and 100%.`;
    }
  }
  return true;
}

/**
 * Validate one entry's `context` when authored: an array of well-formed field names.
 * Absent means the registry row's full allow-list applies.
 */
function validateEntryContext(row: Record<string, unknown>, name: string): void {
  if (row.context === undefined) return;
  if (!Array.isArray(row.context)) {
    throw new CliError(
      `ui_app.surface_point_list["${name}"].context must be an array of field names, e.g. ["recordId"].`,
    );
  }
  const contextCheck = validateUiAppContext(row.context.map(asText));
  if (contextCheck !== true) {
    throw new CliError(`ui_app.surface_point_list["${name}"].context: ${contextCheck}`);
  }
}

/**
 * Validate one entry's `size` when authored. Same authored-or-absent contract as
 * `context`: absent means the host slot's default card size applies; authored, each axis
 * is a px or % CSS length. Shape only, like everything else here — there is no platform
 * per-slot ceiling to check yet.
 */
function validateEntrySize(row: Record<string, unknown>, name: string): void {
  if (row.size === undefined) return;
  const sizeCheck = validateSurfacePointSize(row.size);
  if (sizeCheck !== true) {
    throw new CliError(`ui_app.surface_point_list["${name}"].size: ${sizeCheck}`);
  }
}

/**
 * Validate one entry's CTA fields — its own label, supporting text and destination
 * (BEX-426). Runs per entry because the fields live per entry: an app on three slots
 * shows three labels and opens three URLs, and each violation must name its entry.
 */
function validateEntryCtaFields(
  row: Record<string, unknown>,
  name: string,
  extensionType: string,
): void {
  const at = (field: string) => `ui_app.surface_point_list["${name}"].${field}`;

  const labelCheck = validateUiAppLabel(asText(row.label));
  if (labelCheck !== true) throw new CliError(`${at('label')}: ${labelCheck}`);

  const moreInfoCheck = validateUiAppMoreInfo(asText(row.more_info));
  if (moreInfoCheck !== true) throw new CliError(`${at('more_info')}: ${moreInfoCheck}`);

  if (extensionType === EXTENSION_TYPE_IFRAME) {
    const urlCheck = validateUiAppUrl(asText(row.modal_iframe_url));
    if (urlCheck !== true) throw new CliError(`${at('modal_iframe_url')}: ${urlCheck}`);

    // A modal embeds its URL rather than navigating to it, so there is no link target to
    // set. Refused rather than ignored: the server refuses it per entry as well, and a
    // stored `_blank` on an iframe entry is a field the read path serves and nothing
    // applies.
    if (isPresentField(row.link_target)) {
      throw new CliError(
        `${at('link_target')} has no effect on "${EXTENSION_TYPE_IFRAME}" extensions, which embed their URL in a modal rather than navigating to it. Remove it.`,
      );
    }

    // Refused because the two delivery paths disagree about which URL wins: the
    // widget-card path pairs strictly by extension_type and opens the modal, while the
    // header-menu path routes on redirect_link first and never opens it. The same entry
    // would behave differently depending on the kind of slot it names.
    if (isPresentField(row.redirect_link)) {
      throw new CliError(
        `${at('redirect_link')} cannot be combined with "${EXTENSION_TYPE_IFRAME}": a menu entry would follow the redirect instead of opening the modal, while a card would open the modal. Remove it, or use "${EXTENSION_TYPE_ACTION_LINK}" instead.`,
      );
    }
    return;
  }

  const urlCheck = validateUiAppUrl(asText(row.redirect_link));
  if (urlCheck !== true) throw new CliError(`${at('redirect_link')}: ${urlCheck}`);

  // Optional, and normally absent: `app upload` injects `_blank` onto every actionLink
  // entry, so the authored file carries nothing. A hand-authored value is still checked
  // against the pin — `_self` is refused server-side, so accepting it here would only move
  // the failure to upload time. `undefined` passes; an omitted entry value is defaulted to
  // `_blank` server-side.
  if (row.link_target !== undefined && !UPLOADABLE_LINK_TARGETS.includes(asText(row.link_target))) {
    throw new CliError(
      `Invalid ${at('link_target')} "${asText(row.link_target)}". Must be one of: ${UPLOADABLE_LINK_TARGETS.join(', ')}.`,
    );
  }

  // The UI kit keeps `modal_iframe_url` only for an `iframeExtension` item, so one
  // carried by an actionLink entry is dropped without a word. Reject rather than let a
  // partner ship a URL that will never open.
  if (isPresentField(row.modal_iframe_url)) {
    throw new CliError(
      `${at('modal_iframe_url')} is only used by "${EXTENSION_TYPE_IFRAME}" extensions and is ignored for "${EXTENSION_TYPE_ACTION_LINK}". Remove it, or use redirect_link instead.`,
    );
  }
}

/**
 * Parse and validate an explicit `[account-id]` argument for `app install` /
 * `app uninstall`. Only reached when the positional was actually given — an omitted
 * one is resolved from the authenticated account instead, never routed through here.
 * Brevo account IDs are numeric; accept a trimmed digit string.
 */
export function parseAccountId(value: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    throw new CliError('Invalid account ID: value cannot be empty.');
  }
  if (!/^\d+$/.test(trimmed)) {
    throw new CliError(`Invalid account ID: "${trimmed}" is not a numeric Brevo account ID.`);
  }
  return trimmed;
}

/**
 * Validate that a value is a positive integer.
 * Throws CliError if the value is not a valid positive integer.
 */
export function parsePositiveInt(value: string, flagName: string): number {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) {
    throw new CliError(`Invalid ${flagName}: "${value}" is not a positive integer.`);
  }
  return n;
}

/**
 * Parse and validate a `--app-id` flag value.
 * Accepts any non-empty trimmed string (numeric or UUID).
 */
export function parseAppId(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new CliError('Invalid --app-id: value cannot be empty.');
  }
  return trimmed;
}
